import { z } from 'zod';
import type { FormData } from '../types/form';

// Supported default parameters
export const formDefaultsSchema = z.object({
  // Loan defaults
  loan_purpose: z.enum(['debt_consolidation', 'credit_card_refi', 'emergency', 'home_improvement', 'large_purchases', 'other']).optional(),
  
  // Property defaults
  property_status: z.enum(['own_with_mortgage', 'rent']).optional(),
  
  // Employment defaults
  employment_status: z.enum(['employed', 'employed_full_time', 'employed_part_time', 'military', 'not_employed', 'self_employed', 'retired', 'other']).optional(),
  employment_frequency: z.enum(['weekly', 'biweekly', 'twice_monthly', 'monthly']).optional(),
  
  // Income defaults
  annual_income: z.number().min(0).max(1000000).optional(),
  
  // Education defaults
  education_level: z.enum(['high_school', 'associate', 'bachelors', 'masters', 'doctorate', 'other_grad_degree', 'certificate', 'did_not_graduate', 'still_enrolled', 'other']).optional()
});

export type FormDefaults = z.infer<typeof formDefaultsSchema>;

// Mapping of form defaults to form steps
export const STEP_MAPPING = {
  loan_purpose: 'loan-purpose',
  property_status: 'property-status',
  employment_status: 'employment-status',
  employment_frequency: 'employment-frequency',
  annual_income: 'annual-income',
  education_level: 'education'
} as const;

// Pre-configured default values
export const PRECONFIGURED_DEFAULTS: FormDefaults = {
  loan_purpose: 'debt_consolidation',
  property_status: 'rent',
  employment_status: 'employed_full_time',
  employment_frequency: 'biweekly',
  annual_income: 60000,
  education_level: 'bachelors'
};

export class FormDefaultsService {
  private static instance: FormDefaultsService;
  private defaults: FormDefaults = {};
  private skippedSteps: Set<string> = new Set();
  private initialized: boolean = false;
  private disclaimerShown: boolean = false;

  private constructor() {
     this.skippedSteps.add('education');
    this.defaults.education_level = 'bachelors';
    
    // Check if disclaimer was already shown in this session
    this.disclaimerShown = sessionStorage.getItem('disclaimer_shown') === 'true';
  }

  public static getInstance(): FormDefaultsService {
    if (!FormDefaultsService.instance) {
      FormDefaultsService.instance = new FormDefaultsService();
    }
    return FormDefaultsService.instance;
  }

  /**
   * Initialize form defaults from URL parameters
   */
  public initializeFromUrl(): FormDefaults {
    if (this.initialized) {
      return this.defaults;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const detectedDefaults: Partial<FormDefaults> = {};

    // Check for individual parameter flags
    Object.keys(PRECONFIGURED_DEFAULTS).forEach(key => {
      const paramKey = key as keyof FormDefaults;
      const paramValue = urlParams.get(paramKey);
      const useDefault = urlParams.get(`use_${paramKey}`) === 'true' || urlParams.get(`default_${paramKey}`) === 'true';
      
      if (useDefault) {
        // Use the preconfigured default
        (detectedDefaults as any)[paramKey] = PRECONFIGURED_DEFAULTS[paramKey];
        this.skippedSteps.add(STEP_MAPPING[paramKey]);
      } else if (paramValue) {
        // Use the specific value provided
        try {
          if (paramKey === 'annual_income') {
            const numValue = parseInt(paramValue, 10);
            if (!isNaN(numValue) && numValue > 0) {
              detectedDefaults[paramKey] = numValue;
              this.skippedSteps.add(STEP_MAPPING[paramKey]);
            }
          } else {
            detectedDefaults[paramKey] = paramValue as any;
            this.skippedSteps.add(STEP_MAPPING[paramKey]);
          }
        } catch (error) {
          console.warn(`Invalid value for ${paramKey}:`, paramValue, error);
        }
      }
    });

    // Check for bulk default flag
    const useDefaults = urlParams.get('use_defaults') === 'true' || urlParams.get('skip_form') === 'true';
    if (useDefaults) {
      Object.entries(PRECONFIGURED_DEFAULTS).forEach(([key, value]) => {
        const paramKey = key as keyof FormDefaults;
        if (!detectedDefaults[paramKey]) {
          (detectedDefaults as any)[paramKey] = value;
          this.skippedSteps.add(STEP_MAPPING[paramKey]);
        }
      });
    }

    // Validate and set defaults
    try {
      this.defaults = formDefaultsSchema.parse(detectedDefaults);
      this.initialized = true;
    } catch (error) {
      console.error('Invalid form defaults configuration:', error);
      this.defaults = {};
      this.skippedSteps.clear();
    }

    return this.defaults;
  }

  /**
   * Get current form defaults
   */
  public getDefaults(): FormDefaults {
    if (!this.initialized) {
      return this.initializeFromUrl();
    }
    return this.defaults;
  }

  /**
   * Check if a step should be skipped
   */
  public shouldSkipStep(stepRoute: string): boolean {
    return this.skippedSteps.has(stepRoute);
  }

  /**
   * Get all skipped steps
   */
  public getSkippedSteps(): string[] {
    return Array.from(this.skippedSteps);
  }

  /**
   * Apply defaults to form data
   */
  public applyToFormData(formData: Partial<FormData>): Partial<FormData> {
    const defaults = this.getDefaults();
    
    return {
      ...formData,
      ...(defaults.loan_purpose && { loanPurpose: defaults.loan_purpose }),
      ...(defaults.property_status && { propertyStatus: defaults.property_status }),
      ...(defaults.employment_status && { employmentStatus: defaults.employment_status }),
      ...(defaults.employment_frequency && { employmentFrequency: defaults.employment_frequency }),
      ...(defaults.annual_income && { annualIncome: defaults.annual_income }),
      educationLevel: 'bachelors'
    };
  }

  /**
   * Get analytics data for tracking
   */
  public getAnalyticsData(): Record<string, string> {
    const defaults = this.getDefaults();
    const analytics: Record<string, string> = {};

    if (Object.keys(defaults).length > 0) {
      analytics.has_form_defaults = 'true';
      analytics.skipped_steps_count = this.skippedSteps.size.toString();
      analytics.skipped_steps = Array.from(this.skippedSteps).join(',');
      
      Object.entries(defaults).forEach(([key, value]) => {
        analytics[`default_${key}`] = value?.toString() || '';
      });
    }

    return analytics;
  }

  /**
   * Check if disclaimer should be shown on current step
   * @param currentRoute - The route checking if it should show disclaimer
   */
  public shouldShowDisclaimer(currentRoute?: string): boolean {
    if (!this.disclaimerShown) {
      return true;
    }
    
    // If a route is provided, check if it's the one that showed the disclaimer
    if (currentRoute) {
      const shownRoute = sessionStorage.getItem('disclaimer_shown_route');
      return shownRoute === currentRoute;
    }
    
    return false;
  }

  /**
   * Mark disclaimer as shown on a specific route
   * @param route - The route where disclaimer is being shown
   */
  public markDisclaimerShown(route?: string): void {
    this.disclaimerShown = true;
    sessionStorage.setItem('disclaimer_shown', 'true');
    
    if (route) {
      sessionStorage.setItem('disclaimer_shown_route', route);
    }
  }

  /**
   * Reset the service (useful for testing)
   */
  public reset(): void {
    this.defaults = {};
    this.skippedSteps.clear();
    this.initialized = false;
    this.disclaimerShown = false;
    sessionStorage.removeItem('disclaimer_shown');
    sessionStorage.removeItem('disclaimer_shown_route');
  }
}

export const formDefaultsService = FormDefaultsService.getInstance();